import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LicenceResponse } from '@shared/schemas';

import { pickableLicences } from '@/features/licence/licence-picker';

import type { AddUserDraft } from '../domain/add-user-draft.model';
import type { AddUserError } from '../domain/add-user-error';
import { type CreatedUser, initialState } from './add-user.state';

/**
 * Add-user slice. Reducers are pure state transitions only; every side effect
 * (licence fetch, validation trigger, the create POST) lives in the saga, which
 * dispatches the `save*` transitions below as it progresses.
 */
const slice = createSlice({
  name: 'addUser',
  initialState,
  reducers: {
    /** Record the preset licence (from `?licence=`) and pre-fill the draft. */
    formOpened: (state, action: PayloadAction<string>) => {
      state.presetLicenceId = action.payload;
      if (action.payload) state.draft.licenceId = action.payload;
    },
    /**
     * Store the loaded licences; auto-select the only one the picker offers (a
     * deactivated licence is not offered, so it does not count) when there's no
     * preset and nothing is picked yet.
     */
    licencesLoaded: (state, action: PayloadAction<LicenceResponse[]>) => {
      state.licences = action.payload;
      const pickable = pickableLicences(action.payload);
      if (!state.presetLicenceId && !state.draft.licenceId && pickable.length === 1) {
        state.draft.licenceId = pickable[0].id;
      }
    },
    /**
     * A licence was just created from the picker (#593): it becomes the pick
     * straight away. The saga reloads the licences so the picker and the summary
     * can show it; `licencesLoaded` then keeps this pick.
     */
    licenceCreated: (state, action: PayloadAction<string>) => {
      state.draft.licenceId = action.payload;
      delete state.validationErrors.licenceId;
    },
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof AddUserDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state, action: PayloadAction<CreatedUser>) => {
      state.saveStatus = 'succeeded';
      state.created = action.payload;
    },
    saveFailed: (state, action: PayloadAction<AddUserError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to the initial state (after success / on unmount). */
    resetForm: () => initialState,
  },
});

export const addUserActions = slice.actions;
export const addUserReducer = slice.reducer;
